import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ValueArtComponent, ValueArtId } from './value-art.component';

export interface ValueItem {
  id: ValueArtId;
  /** 01–08, shown as the card's index. */
  no: string;
  title: string;
  copy: string;
  /** Which brand accent this card leans on. */
  tone: 'pink' | 'violet' | 'blue';
}

@Component({
  selector: 'app-value-card',
  standalone: true,
  imports: [CommonModule, ValueArtComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './value-card.component.html',
  styleUrls: ['./value-card.component.scss'],
})
export class ValueCardComponent {
  @Input({ required: true }) item!: ValueItem;

  /** The one open card in the grid. */
  @Input() active = false;

  /** Another card is open, so this one steps back. */
  @Input() dimmed = false;

  @Output() activate = new EventEmitter<void>();

  @HostBinding('class.is-active') get activeClass() {
    return this.active;
  }

  @HostBinding('class.is-dimmed') get dimmedClass() {
    return this.dimmed && !this.active;
  }

  @HostBinding('attr.data-tone') get toneAttr() {
    return this.item?.tone;
  }

  @HostBinding('attr.tabindex') readonly tabindex = '0';
  @HostBinding('attr.role') readonly role = 'button';

  @HostBinding('attr.aria-expanded') get expanded() {
    return String(this.active);
  }

  constructor(private host: ElementRef<HTMLElement>) {}

  /** Pointer position feeds the border sheen and the tilt. */
  @HostListener('pointermove', ['$event'])
  onMove(e: PointerEvent): void {
    const el = this.host.nativeElement;
    const r = el.getBoundingClientRect();
    el.style.setProperty('--mx', `${((e.clientX - r.left) / r.width) * 100}%`);
    el.style.setProperty('--my', `${((e.clientY - r.top) / r.height) * 100}%`);
  }

  @HostListener('click')
  onClick(): void {
    this.activate.emit();
  }

  @HostListener('keydown.enter', ['$event'])
  @HostListener('keydown.space', ['$event'])
  onKey(e: Event): void {
    e.preventDefault();
    this.activate.emit();
  }
}
